import React, { useState, useEffect, useRef, useCallback } from 'react';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { ArrowLeft } from 'lucide-react';
import { HeaderContainer, HeaderContent, TitleContainer, BackButton, Title, TitleWrapper, Subtitle, ActionsContainer, RightContentContainer } from './styles/pageheader.styles';
import { useThrottle } from './hooks/useThrottle';
import { css } from '@emotion/react';
/**
 * 페이지 헤더 컴포넌트
 * 
 * 스크롤 방향에 따라 투명도와 위치가 변경되는 페이지 상단 헤더 컴포넌트입니다.
 * 다크 테마를 지원합니다.
 * (VSCode 환경에서는 고정 헤더로 표시됩니다)
 */ const PageHeader = ({ title, subtitle, showBackButton = false, onBackClick, actions, rightContent, useScrollAnimation = true, className })=>{
    // 헤더 상태 관리
    const [lastScrollY, setLastScrollY] = useState(0);
    const [opacity, setOpacity] = useState(1); // 1.0 = 완전 불투명, 0.1 = 최대 투명도
    const [translateY, setTranslateY] = useState(0); // 위로 이동하는 픽셀 값 (0 = 원래 위치)
    const [isVisible, setIsVisible] = useState(true);
    const headerRef = useRef(null);
    // VSCode 환경 감지
    const [isVSCodeEnv, setIsVSCodeEnv] = useState(false);
    // 화면 높이 감지 상태 추가
    const [compactMode, setCompactMode] = useState('normal');
    const [screenHeight, setScreenHeight] = useState(0);
    // 화면 높이 감지 함수
    const checkScreenHeight = useCallback(()=>{
        const height = window.innerHeight;
        setScreenHeight(height); // 디버깅용 상태 저장
        // 콘솔에 현재 높이와 모드 출력
        console.log(`[PageHeader] 현재 화면 높이: ${height}px`);
        let newMode = 'normal';
        if (height <= 500) {
            newMode = 'ultra-compact';
        } else if (height <= 600) {
            newMode = 'very-compact';
        } else if (height <= 700) {
            newMode = 'compact';
        } else {
            newMode = 'normal';
        }
        if (newMode !== compactMode) {
            console.log(`[PageHeader] 모드 변경: ${compactMode} → ${newMode}`);
            setCompactMode(newMode);
        }
    }, [
        compactMode
    ]);
    useEffect(()=>{
        // VSCode 환경 감지 (간소화)
        const isVSCode = document.body.classList.contains('vscode-dark') || document.body.classList.contains('vscode-light') || document.documentElement.classList.contains('vscode-dark') || document.documentElement.classList.contains('vscode-light') || typeof acquireVsCodeApi !== 'undefined';
        setIsVSCodeEnv(isVSCode);
        // 초기 실행
        setTimeout(()=>{
            checkScreenHeight();
        }, 100);
        // 리사이즈 이벤트에 연결
        window.addEventListener('resize', checkScreenHeight);
        return ()=>{
            window.removeEventListener('resize', checkScreenHeight);
        };
    }, [
        checkScreenHeight
    ]);
    // 컴팩트 모드가 변경될 때마다 로그 출력
    useEffect(()=>{
        console.log(`[PageHeader] 현재 적용된 모드: ${compactMode}, 화면 높이: ${screenHeight}px`);
    }, [
        compactMode,
        screenHeight
    ]);
    // 스크롤 처리 함수
    const handleScroll = useCallback(()=>{
        const currentScrollY = window.scrollY;
        const scrollingDown = currentScrollY > lastScrollY;
        const scrollDiff = Math.abs(currentScrollY - lastScrollY);
        if (scrollDiff < 2) return; // 미세한 변화는 무시
        if (scrollingDown) {
            // 아래로 스크롤 중
            if (currentScrollY > 30) {
                // 1단계: 먼저 투명해짐
                if (opacity > 0.1) {
                    setOpacity((prev)=>Math.max(0.1, prev - Math.min(0.1, scrollDiff * 0.01)));
                } else {
                    setTranslateY((prev)=>{
                        const newValue = Math.min(100, prev + Math.min(10, scrollDiff * 0.5));
                        if (newValue >= 100) {
                            setIsVisible(false);
                        }
                        return newValue;
                    });
                }
            }
        } else {
            // 위로 스크롤 중
            setIsVisible(true);
            // 1단계: 먼저 위치 복원
            if (translateY > 0) {
                setTranslateY((prev)=>Math.max(0, prev - Math.min(10, scrollDiff * 0.8)));
            } else {
                setOpacity((prev)=>Math.min(1.0, prev + Math.min(0.1, scrollDiff * 0.015)));
            }
        }
        setLastScrollY(currentScrollY);
    }, [
        lastScrollY,
        opacity,
        translateY
    ]);
    // 스로틀된 스크롤 핸들러
    const throttledScrollHandler = useThrottle(handleScroll, 50);
    // 스크롤 이벤트 핸들링 (VSCode 환경이 아닐 때만)
    useEffect(()=>{
        if (!useScrollAnimation || isVSCodeEnv) return;
        // 스크롤 이벤트 리스너 등록
        window.addEventListener('scroll', throttledScrollHandler, {
            passive: true
        });
        return ()=>{
            window.removeEventListener('scroll', throttledScrollHandler);
        };
    }, [
        throttledScrollHandler,
        isVSCodeEnv,
        useScrollAnimation
    ]);
    // 뒤로가기 핸들러
    const handleBackClick = ()=>{
        if (onBackClick) {
            onBackClick();
        } else {
            // 기본 동작 (브라우저 히스토리 뒤로가기)
            if (typeof window !== 'undefined') {
                window.history.back();
            }
        }
    };
    // 애니메이션 props 계산
    const getAnimationProps = ()=>{
        if (!useScrollAnimation || isVSCodeEnv) return {};
        return {
            $opacity: opacity,
            $translateY: translateY,
            $isVisible: isVisible || translateY < 100
        };
    };
    // 헤더 높이 스타일 계산
    const getHeaderHeightStyle = ()=>{
        switch(compactMode){
            case 'ultra-compact':
                return css`
          padding: 0.15rem 0.5rem;
          min-height: 28px;
        `;
            case 'very-compact':
                return css`
          padding: 0.2rem 0.6rem;
          min-height: 32px;
        `;
            case 'compact':
                return css`
          padding: 0.35rem 0.75rem;
          min-height: 42px;
        `;
            default:
                return css``;
        }
    };
    return /*#__PURE__*/ React.createElement(HeaderContainer, {
        ref: headerRef,
        className: `${isVSCodeEnv ? 'vscode-header' : ''} ${className || ''} ${compactMode}`,
        ...getAnimationProps(),
        css: getHeaderHeightStyle(),
        "data-vscode": isVSCodeEnv ? 'true' : 'false',
        "data-animation": useScrollAnimation && !isVSCodeEnv ? 'true' : 'false',
        "data-compact-mode": compactMode,
        "data-screen-height": screenHeight
    }, /*#__PURE__*/ React.createElement(HeaderContent, {
        css: css`
        gap: ${compactMode === 'ultra-compact' ? '0.2rem' : compactMode === 'very-compact' ? '0.25rem' : compactMode === 'compact' ? '0.35rem' : '0.5rem'};
      `
    }, /*#__PURE__*/ React.createElement(TitleContainer, null, showBackButton && /*#__PURE__*/ React.createElement(BackButton, {
        onClick: handleBackClick,
        "aria-label": "뒤로 가기",
        css: css`
                margin-right: ${compactMode === 'ultra-compact' ? '0.25rem' : compactMode === 'very-compact' ? '0.35rem' : compactMode === 'compact' ? '0.5rem' : '1rem'};
                padding: ${compactMode === 'ultra-compact' ? '0.15rem' : compactMode === 'very-compact' ? '0.2rem' : compactMode === 'compact' ? '0.3rem' : '0.5rem'};
              `
    }, /*#__PURE__*/ React.createElement(ArrowLeft, {
        size: compactMode === 'ultra-compact' ? 14 : compactMode === 'very-compact' ? 16 : compactMode === 'compact' ? 18 : 24
    })), subtitle ? /*#__PURE__*/ React.createElement(TitleWrapper, null, /*#__PURE__*/ React.createElement(Title, {
        css: css`
                font-size: ${compactMode === 'ultra-compact' ? '0.85rem' : compactMode === 'very-compact' ? '0.95rem' : compactMode === 'compact' ? '1.1rem' : '1.25rem'};
                padding: ${compactMode === 'ultra-compact' ? '0.15rem 0' : compactMode === 'very-compact' ? '0.2rem 0' : compactMode === 'compact' ? '0.25rem 0' : '0.5rem 0'};
              `
    }, title), /*#__PURE__*/ React.createElement(Subtitle, {
        css: css`
                font-size: ${compactMode === 'ultra-compact' ? '0.6875rem' : compactMode === 'very-compact' ? '0.75rem' : compactMode === 'compact' ? '0.8125rem' : '0.875rem'};
              `
    }, subtitle)) : /*#__PURE__*/ React.createElement(Title, {
        css: css`
              font-size: ${compactMode === 'ultra-compact' ? '0.85rem' : compactMode === 'very-compact' ? '0.95rem' : compactMode === 'compact' ? '1.1rem' : '1.25rem'};
              padding: ${compactMode === 'ultra-compact' ? '0.15rem 0' : compactMode === 'very-compact' ? '0.2rem 0' : compactMode === 'compact' ? '0.25rem 0' : '0.5rem 0'};
            `
    }, title)), /*#__PURE__*/ React.createElement(ActionsContainer, {
        css: css`
          transform: ${compactMode === 'ultra-compact' ? 'scale(0.7)' : compactMode === 'very-compact' ? 'scale(0.8)' : compactMode === 'compact' ? 'scale(0.9)' : 'none'};
          transform-origin: right center;
          gap: ${compactMode === 'ultra-compact' ? '0.2rem' : compactMode === 'very-compact' ? '0.25rem' : compactMode === 'compact' ? '0.35rem' : '0.5rem'};
        `
    }, actions, rightContent && /*#__PURE__*/ React.createElement(RightContentContainer, null, rightContent))));
};
export default PageHeader;
